import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, Image as ImageIcon, Loader2, ArrowLeft } from 'lucide-react';
import ScrollReveal from './ScrollReveal';
import { companyData } from '../data';

const categories = [
  { label: 'Alle', slug: 'all' },
  { label: 'Badeværelse', slug: 'badevaerelse' },
  { label: 'Renovering', slug: 'renovering' },
  { label: 'VVS', slug: 'vvs' },
  { label: 'Totalentreprise', slug: 'totalentreprise' },
];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [visibleCount, setVisibleCount] = useState(6);

  // Set SEO Title and Scroll to Top on mount
  useEffect(() => {
    document.title = 'Projektgalleri | Bust Byg & Renovering ApS';
    window.scrollTo(0, 0);
    return () => {
      document.title = companyData.seo.title;
    };
  }, []);

  // Fetch images from CMS
  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = 'https://api.dev.auxo.dk/public/v2/content/gallery_images';
        if (activeCategory !== 'all') {
          url += `?term=project-category:${activeCategory}`;
        }
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Kunne ikke hente billeder fra galleriet.');
        }
        const data = await response.json();
        
        setImages(data.items || []);
        setVisibleCount(6); // Reset visible count on category change
      } catch (err) {
        console.error('Error fetching gallery images:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [activeCategory]);

  const displayedImages = images.slice(0, visibleCount);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 6);
  };

  // Lightbox Navigation
  const handlePrev = (e) => {
    e.stopPropagation();
    setLightboxIndex((prevIndex) => (prevIndex === 0 ? displayedImages.length - 1 : prevIndex - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setLightboxIndex((prevIndex) => (prevIndex === displayedImages.length - 1 ? 0 : prevIndex + 1));
  };

  // Keyboard Navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowLeft') handlePrev(e);
      if (e.key === 'ArrowRight') handleNext(e);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, displayedImages]);

  return (
    <div className="bg-[#111111] text-white min-h-screen">
      {/* Hero Banner for Gallery */}
      <section className="relative py-20 md:py-32 overflow-hidden border-b border-gray-900">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/39491458-c5e4-4280-8295-09ca3e3ec516/5a209088-c769-4044-b3b0-c69b9459c1c3.jpg"
            alt="Bust Byg Galleri"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm font-semibold uppercase tracking-wider"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Tilbage til forsiden</span>
          </Link>

          <ScrollReveal direction="up" duration={800}>
            <div>
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-white uppercase">
                Projektgalleri
              </h1>
              <p className="mt-4 text-lg md:text-xl text-gray-300 max-w-3xl font-light leading-relaxed">
                Her kan du se et bredt udvalg af vores udførte projekter. Vi sætter en ære i det gode håndværk og leverer altid et resultat, der holder.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Gallery Grid Section */}
      <section className="py-16 md:py-24 bg-gray-50 text-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Category Filters */}
          <ScrollReveal direction="up" duration={700}>
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => setActiveCategory(category.slug)}
                  className={`px-5 py-2.5 rounded-none text-xs font-bold uppercase tracking-wider transition-all duration-200 border ${
                    activeCategory === category.slug
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-white border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Gallery Grid */}
          {loading ? (
            // Skeleton Loader Grid
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-video bg-gray-100 border border-gray-200 rounded-none animate-pulse flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-orange-500/40 animate-spin" />
                </div>
              ))}
            </div>
          ) : error ? (
            // Error State
            <div className="text-center py-12 bg-white border border-red-200 rounded-none max-w-lg mx-auto">
              <p className="text-red-500 font-semibold">{error}</p>
              <button
                onClick={() => setActiveCategory('all')}
                className="mt-4 text-xs font-bold uppercase text-gray-900 hover:text-gray-600"
              >
                Prøv igen
              </button>
            </div>
          ) : images.length === 0 ? (
            // Empty State
            <div className="text-center py-16 bg-white border border-gray-200 rounded-none max-w-lg mx-auto space-y-3">
              <ImageIcon className="w-12 h-12 text-gray-400 mx-auto" />
              <p className="text-gray-500 font-medium">Ingen billeder fundet i denne kategori.</p>
            </div>
          ) : (
            // Image Grid
            <div className="space-y-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayedImages.map((item, index) => {
                  const imageUrl = Array.isArray(item.image) ? item.image[0] : item.image;
                  return (
                    <ScrollReveal
                      key={item.id}
                      direction="up"
                      duration={600}
                      delay={(index % 6) * 100}
                    >
                      <div
                        onClick={() => setLightboxIndex(index)}
                        className="group relative aspect-[4/3] bg-white border border-gray-200 hover:border-orange-500/40 rounded-none overflow-hidden cursor-pointer shadow-md transition-all duration-300"
                      >
                        <img
                          src={imageUrl}
                          alt={item.title?.da || 'Bust Byg & Renovering'}
                          className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                    </ScrollReveal>
                  );
                })}
              </div>

              {/* Load More Button */}
              {images.length > visibleCount && (
                <div className="text-center pt-4">
                  <button
                    onClick={handleLoadMore}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-none text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:-translate-y-0.5"
                  >
                    Vis flere billeder
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-6 right-6 p-2 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full z-50"
            aria-label="Luk visning"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-4 sm:left-6 p-3 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full z-50"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 sm:right-6 p-3 text-gray-400 hover:text-white transition-colors bg-white/5 hover:bg-white/10 rounded-full z-50"
            aria-label="Næste billede"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Image & Caption Container */}
          <div
            className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={Array.isArray(displayedImages[lightboxIndex]?.image) ? displayedImages[lightboxIndex].image[0] : displayedImages[lightboxIndex]?.image}
              alt={displayedImages[lightboxIndex]?.title?.da || 'Bust Byg & Renovering'}
              className="max-w-full max-h-[75vh] object-contain rounded-none border border-gray-800 shadow-2xl"
            />
            {/* Caption */}
            <div className="mt-4 text-center space-y-1 px-4">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                {categories.find(c => c.slug === displayedImages[lightboxIndex]?.terms?.['project-category']?.[0])?.label || 'Håndværk'}
              </span>
              <h4 className="text-lg font-bold text-white uppercase">
                {displayedImages[lightboxIndex]?.title?.da || 'Projekt'}
              </h4>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
